/**
 * @component CollegesTable
 * @description
 * Componente principal que muestra la tabla de colegios registrados.
 * Permite visualizar, crear, editar y eliminar colegios mediante un modal.
 * Usa `AdvanceTableWrapper`, `AdvanceTable`, y `SweetAlert` para interacción avanzada.
 */

import React, { useEffect, useState, useCallback } from 'react';
import { useSelector } from 'react-redux';

import { Row, Col, Card } from 'react-bootstrap';
import { Modal } from 'bootstrap';
import Swal from 'sweetalert2';
import BeatLoader from 'react-spinners/BeatLoader';

import { handleApiError, successNotification } from 'utils';
import { fetchColleges, deleteCollegeById } from 'api/colleges/colleges';

import AdvanceTableWrapper from 'components/common/advance-table/AdvanceTableWrapper';
import AdvanceTable from 'components/common/advance-table/AdvanceTable';
import AdvanceTablePagination from 'components/common/advance-table/AdvanceTablePagination';

import CollegesColumns from './columns/CollegesColumns';
import Header from './header/Header';
import CollegeModal from '../modal/CollegeModal';

const override = {
	display: "block",
	margin: "0 auto",
	borderColor: "red"
};

const CollegesTable = () => {
	// Token de autenticación
	const token = useSelector(state => state.authentication.user?.token);

	// Datos y tabla
	const [colleges, setColleges] = useState([]); 
	const [rows, setRows] = useState([]); 

	// Modal control
	const [isModalOpen, setIsModalOpen] = useState(false);
	const [activeModal, setActiveModal] = useState("");

	// Control de acción (Nuevo/Editar) y ID seleccionado
	const [action, setAction] = useState("");
	const [collegeId, setCollegeId] = useState(0);

	const [loading, setLoading] = useState(false);

	/**
	 * Transforma la lista de colegios a un formato legible por la tabla
	 */
	useEffect(() => {
		if (colleges.length > 0) {
			let index = 1;
			const formattedColleges = colleges.map(college => ({
				index: index++, 
				id: college.id,
				name: college.name,
				description: college.description || '',
				email: college.email || 'Sin información',
				phone: college.phone || 'Sin información',
				emet: college.emet, 
				epst: college.epst 
			}));
			setRows(formattedColleges);
		}
	}, [colleges]);

	/**
	 * Abre automáticamente el modal al activar el estado
	 */
	useEffect(() => {
		if(isModalOpen && activeModal === 'Colegio') {
			let myModal = new Modal(document.getElementById('collegeModal'));
			myModal.show();
		}
	}, [isModalOpen, activeModal]);

	/**
	 * Llama al backend para obtener todos los colegios
	 */
	const loadColleges = useCallback(async () => {
		try {
			setLoading(true);
			const { data } = await fetchColleges({}, token);
			setColleges(data);
		} catch (error) {
			handleApiError(error);
			setColleges([]); // En caso de error también aseguras consistencia
		} finally {
			setLoading(false);
		}
	}, [token]);

	/**
	 * useEffect que carga los colegios al montar el componente
	 */
	useEffect(() => {
		if (token) loadColleges();
	}, [token, loadColleges]);

	/**
	 * Elimina un colegio por ID y recarga la lista.
	 * @param {number} id - ID del colegio a eliminar
	 */
	const handleDeleteCollege = async (id) => {
		try {
			const { data } = await deleteCollegeById(id, token);
			loadColleges();
			successNotification(data.message);
		} catch (error) {
			handleApiError(error);
		}
	};

	/**
	 * Prepara el modal para editar un colegio existente
	 * @param {number} id - ID del colegio a editar
	 */
	const handleEdit = (id) => {
		setIsModalOpen(true);
		setActiveModal("Colegio");
		setAction("Editar");
		setCollegeId(id);
	};

	/**
	 * Abre alerta para confirmar eliminación
	 * @param {number} id - ID del colegio
	 * @param {string} name - Nombre del colegio
	 */
	const handleDelete = (id, name) => {
		Swal.fire({
			title: `¿Deseas eliminar este colegio: ${name}?`,
			text: "Una vez eliminado ya no estará disponible este colegio.",
			icon: "question",
			confirmButtonText: 'Continuar',
			cancelButtonText: 'Cancelar',
			showCancelButton: true,
			showCloseButton: true,
			allowOutsideClick: false,
			allowEscapeKey: false,
			allowEnterKey: false,
			reverseButtons : true
		}).then((result) => {
			if (result.isConfirmed) confirmDeleteCollege(id);
		});
	};

	/**
	 * Confirma eliminación y ejecuta acción
	 * @param {number} id - ID del colegio a eliminar
	 */
	const confirmDeleteCollege = (id) => {
		Swal.fire({
			title: "¿Estas seguro?",
			text: "Esta operación no se puede deshacer.",
			icon: "warning",
			confirmButtonText: 'Eliminar',
			cancelButtonText: 'Cancelar',
			showCancelButton: true,
			showCloseButton: true,
			allowOutsideClick: false,
			allowEscapeKey: false,
			allowEnterKey: false,
			reverseButtons : true
		}).then((result) => {
			if (result.isConfirmed) handleDeleteCollege(id);
		});
	};

	/**
	 * Abre el modal en modo 'Nuevo'
	 */
	const handleOpenModal = () => {
		setIsModalOpen(true);
		setActiveModal("Colegio");
		setAction("Nuevo");
		setCollegeId(0);
	};

	/**
	 * Cierra el modal y recarga los colegios
	 */
	const handleCloseModal = () => {
		setIsModalOpen(false);
		setActiveModal("");
		document.body.style.overflow = 'auto';
		loadColleges();
	};

	// Renderizado
	return (
		<Row className="gx-3">
			<Col xxl={12} xl={12}>
				<AdvanceTableWrapper
					columns={CollegesColumns(handleEdit, handleDelete)}
					data={rows}
					selection
					selectionColumnWidth={52}
					sortable
					pagination
					perPage={11}
					rowCount={rows.length}>
					<Card>
						<Card.Header className="px-0">
							<Header table handleOpen={handleOpenModal} />
						</Card.Header>
						<Card.Body className="p-0">
							{ loading ? (
								<div className="sweet-loading" style={{
									position: 'fixed',
									top: 0,
									left: 0,
									width: '100vw',
									height: '100vh',
									display: 'flex',
									justifyContent: 'center',
									alignItems: 'center',
									backgroundColor: 'rgba(255, 255, 255, 0.8)', // Puedes agregar un fondo semitransparente si lo deseas
									zIndex: 9999}} >
									<BeatLoader
										color={"#0a83e6"}
										loading={loading}
										cssOverride={override}
										size={15}
										margin={5}
										aria-label="Loading..."
										data-testid="loader"
									/>
								</div>
							) : (
								<AdvanceTable
									table
									headerClassName="bg-light text-800 align-middle"
									rowClassName="btn-reveal-trigger align-middle"
									tableProps={{
										size: 'sm',
										className: 'fs--1 mb-0 overflow-hidden'
									}}
								/>
							)}
						</Card.Body>
						<Card.Footer>
							<AdvanceTablePagination table />
						</Card.Footer>
					</Card>
				</AdvanceTableWrapper>
			</Col>

			{ isModalOpen && activeModal === "Colegio" && (
				<CollegeModal college_id={collegeId} action={action} handleClose={handleCloseModal} />
			)}
		</Row>
	);
};

export default CollegesTable;